/**
 * Auditoría SEO 06/2026 · Página de ciudad (`/ciudad/:city` y `/ciudad/:city/:categoria`).
 *
 * Objetivos:
 *  - Cobertura interna 100%: lista TODOS los centros públicos de la ciudad
 *    (sin el límite de 60 del marketplace) con enlaces <a> a /centro/:slug.
 *  - SEO programático: title/description/canonical dinámicos + JSON-LD
 *    (BreadcrumbList + ItemList).
 *  - Preparada para escalar: Lleida, Barcelona, etc. aparecen automáticamente
 *    cuando existan centros con esa ciudad.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import { useStaticSeo } from "../lib/seo";
import GlobalHeader from "../components/layout/GlobalHeader";
import { StarRating } from "./components/StarRating";
import { MapPin, Storefront, ArrowRight, Sparkle, CaretRight } from "@phosphor-icons/react";

const pub = axios.create({ baseURL: `${BACKEND_URL}/api` });

export default function CityLanding() {
  const { city: citySlug, categoria } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!citySlug) return;
    setLoading(true);
    setNotFound(false);
    const params = categoria ? `?sector=${encodeURIComponent(categoria)}` : "";
    pub.get(`/marketplace/city/${citySlug}${params}`)
      .then(({ data }) => setData(data))
      .catch(() => { setData(null); setNotFound(true); })
      .finally(() => setLoading(false));
  }, [citySlug, categoria]);

  const cityName = data?.city || "";
  const sectorLabel = data?.sector_label || "";
  const total = data?.total || 0;

  const seoTitle = cityName
    ? (sectorLabel
        ? `${sectorLabel} en ${cityName} · Reserva online | AutoGlow`
        : `Talleres y centros del motor en ${cityName} · Reserva online | AutoGlow`)
    : "Centros por ciudad | AutoGlow";
  const seoDescription = cityName
    ? `Encuentra y reserva online en ${total} ${total === 1 ? "centro" : "centros"}${sectorLabel ? ` de ${sectorLabel.toLowerCase()}` : " del motor"} en ${cityName}. Precios, valoraciones reales y confirmación inmediata.`
    : "Descubre centros profesionales del motor por ciudad en AutoGlow.";
  const seoPath = categoria ? `/ciudad/${citySlug}/${categoria}` : `/ciudad/${citySlug}`;

  const jsonLd = useMemo(() => {
    if (!data?.found) return null;
    const origin = window.location.origin;
    const crumbs = [
      { "@type": "ListItem", position: 1, name: "Marketplace", item: `${origin}/marketplace` },
      { "@type": "ListItem", position: 2, name: data.city, item: `${origin}/ciudad/${data.city_slug}` },
    ];
    if (data.sector_label) {
      crumbs.push({ "@type": "ListItem", position: 3, name: data.sector_label, item: `${origin}/ciudad/${data.city_slug}/${data.sector}` });
    }
    return [
      { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: seoTitle,
        numberOfItems: data.items.length,
        itemListElement: data.items.slice(0, 50).map((o, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: o.name,
          url: `${origin}/centro/${o.slug}`,
        })),
      },
    ];
  }, [data, seoTitle]);

  useStaticSeo({ title: seoTitle, description: seoDescription, path: seoPath, jsonLd });

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)]">
        <GlobalHeader variant="auto" testId="city-header" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="city-loading">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="ag-card p-5 animate-pulse">
              <div className="h-32 bg-[var(--ag-midnight)] rounded mb-4" />
              <div className="h-5 w-2/3 bg-[var(--ag-midnight)] rounded mb-2" />
              <div className="h-3 w-full bg-[var(--ag-midnight)] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (notFound || !data?.found) {
    return (
      <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)]">
        <GlobalHeader variant="auto" testId="city-header" />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="city-404">
          <MapPin size={48} weight="duotone" className="text-[var(--ag-text-dim)] mx-auto" />
          <h1 className="font-display font-black text-3xl tracking-tighter mt-5">
            Aún no hay centros en esta ciudad
          </h1>
          <p className="text-[var(--ag-text-dim)] mt-3">
            Estamos creciendo. Mientras tanto, explora todos los centros disponibles en el marketplace.
          </p>
          <Link to="/marketplace" className="ag-btn-gold h-11 px-6 mt-6 inline-flex items-center gap-2 rounded-md font-bold text-sm" data-testid="city-404-marketplace-link">
            <Storefront size={16} weight="fill" /> Ver marketplace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)] flex flex-col" data-testid={`city-landing-${data.city_slug}`}>
      <GlobalHeader variant="auto" testId="city-header" />

      {/* Hero */}
      <section className="ag-grid border-b border-[var(--ag-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)]" data-testid="city-breadcrumb">
            <Link to="/marketplace" className="hover:text-[var(--ag-gold)] transition-colors">Marketplace</Link>
            <CaretRight size={10} />
            {categoria ? (
              <>
                <Link to={`/ciudad/${data.city_slug}`} className="hover:text-[var(--ag-gold)] transition-colors" data-testid="city-breadcrumb-city">{data.city}</Link>
                <CaretRight size={10} />
                <span className="text-[var(--ag-text)] font-semibold">{data.sector_label}</span>
              </>
            ) : (
              <span className="text-[var(--ag-text)] font-semibold">{data.city}</span>
            )}
          </nav>

          <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter mt-5 leading-tight" data-testid="city-title">
            {sectorLabel ? (
              <>{sectorLabel} en <span className="text-[var(--ag-gold)]">{data.city}</span></>
            ) : (
              <>Talleres y centros del motor en <span className="text-[var(--ag-gold)]">{data.city}</span></>
            )}
          </h1>
          <p className="text-[var(--ag-text-dim)] mt-4 max-w-2xl" data-testid="city-subtitle">
            {total} {total === 1 ? "centro disponible" : "centros disponibles"}
            {sectorLabel ? ` de ${sectorLabel.toLowerCase()}` : ""} en {data.city}.
            Compara valoraciones reales y reserva online con confirmación inmediata.
          </p>

          {/* Chips de subsector */}
          {data.sectors?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-6" data-testid="city-sector-chips">
              <Link
                to={`/ciudad/${data.city_slug}`}
                className={`px-3 py-1.5 rounded-sm border text-[11px] uppercase tracking-wider transition-colors ${!categoria ? "bg-[var(--ag-gold)] text-black border-transparent font-bold" : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
                data-testid="city-sector-chip-all"
              >
                Todos
              </Link>
              {data.sectors.map((s) => (
                <Link
                  key={s.id}
                  to={`/ciudad/${data.city_slug}/${s.id}`}
                  className={`px-3 py-1.5 rounded-sm border text-[11px] uppercase tracking-wider transition-colors ${categoria === s.id ? "bg-[var(--ag-gold)] text-black border-transparent font-bold" : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
                  data-testid={`city-sector-chip-${s.id}`}
                >
                  {s.label} <span className="opacity-60">({s.count})</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Listado de centros */}
      <section className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 flex-1">
        {data.items.length === 0 ? (
          <div className="ag-card p-12 text-center" data-testid="city-empty">
            <Storefront size={48} weight="duotone" className="text-[var(--ag-text-dim)] mx-auto" />
            <h2 className="font-display font-bold text-xl mt-4">Sin centros en esta categoría</h2>
            <p className="text-sm text-[var(--ag-text-dim)] mt-2">
              Prueba con otra categoría o <Link to={`/ciudad/${data.city_slug}`} className="text-[var(--ag-gold)] underline">ver todos los centros de {data.city}</Link>.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.items.map((o) => (
              <div key={o.slug} className="ag-card overflow-hidden hover:border-[var(--ag-gold)]/70 hover:-translate-y-0.5 transition-all duration-300 group flex flex-col relative" data-testid={`city-card-${o.slug}`}>
                <div className="absolute top-0 left-0 right-0 h-1 z-10" style={{ backgroundColor: o.brand_color || "#0033A0" }} aria-hidden />
                <Link to={`/centro/${o.slug}`} className="block relative" data-testid={`city-card-link-${o.slug}`}>
                  <div
                    className="h-40 bg-[var(--ag-midnight)] relative overflow-hidden"
                    style={o.hero_image_url ? {
                      backgroundImage: `linear-gradient(to bottom, rgba(26,26,26,0.05) 30%, rgba(26,26,26,0.85) 100%), url("${o.hero_image_url}")`,
                      backgroundSize: "cover", backgroundPosition: "center",
                    } : {}}
                  >
                    {!o.hero_image_url && (
                      <div className="absolute inset-0 flex items-center justify-center text-[var(--ag-gold)] opacity-25">
                        <Storefront size={48} weight="duotone" />
                      </div>
                    )}
                    <div className="absolute bottom-3 left-4">
                      {o.logo_url ? (
                        <img src={o.logo_url} alt={o.name} className="w-12 h-12 rounded-lg object-contain bg-white p-1 border border-white/30 shadow-xl" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-[var(--ag-gold)] text-[var(--ag-midnight)] grid place-items-center font-display font-black text-xl border border-white/30 shadow-xl">
                          {o.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
                <div className="px-5 pt-4 pb-5 flex flex-col gap-3 flex-1">
                  <Link to={`/centro/${o.slug}`} className="block">
                    <div className="font-display font-extrabold text-lg leading-tight tracking-tight group-hover:text-[var(--ag-gold)] transition-colors">{o.name}</div>
                    {(o.locality || o.address) && (
                      <div className="text-[11px] text-[var(--ag-text-dim)] mt-1.5 flex items-center gap-1.5 uppercase tracking-wider font-medium">
                        <MapPin size={11} weight="duotone" /> {o.locality || o.address}
                      </div>
                    )}
                    <div className="mt-2">
                      {o.rating_count > 0 ? (
                        <StarRating value={o.rating_avg} count={o.rating_count} size={12} />
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]/55">Aún sin reseñas</span>
                      )}
                    </div>
                  </Link>
                  {o.description && (
                    <p className="text-[12px] text-[var(--ag-text-dim)] leading-relaxed line-clamp-2">{o.description}</p>
                  )}
                  {o.sectors?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {o.sectors.slice(0, 3).map((s) => (
                        <span key={s} className="text-[9px] uppercase tracking-wider bg-[var(--ag-midnight-soft)]/70 border border-[var(--ag-border)] text-[var(--ag-text-dim)] px-2 py-0.5 rounded-sm font-semibold">{s}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-auto pt-3 border-t border-[var(--ag-border)] flex items-center justify-between gap-2">
                    <Link to={`/centro/${o.slug}`} className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[var(--ag-text-dim)] hover:text-white transition-colors inline-flex items-center gap-1" data-testid={`city-centro-link-${o.slug}`}>
                      <Storefront size={11} weight="duotone" /> Ver centro
                    </Link>
                    <Link to={`/reservar/${o.slug}`} className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ag-gold)] inline-flex items-center gap-2 group-hover:gap-3 transition-all" data-testid={`city-cta-${o.slug}`}>
                      Reservar <ArrowRight size={13} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA pro */}
      <section className="border-t border-[var(--ag-border)] bg-[var(--ag-midnight-soft)]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 text-center">
          <Sparkle size={28} weight="fill" className="text-[var(--ag-gold)] mx-auto" />
          <h2 className="font-display font-black text-2xl mt-4 tracking-tighter">¿Tienes un centro en {data.city}?</h2>
          <p className="text-[var(--ag-text-dim)] mt-2 text-sm">Crea tu perfil en AutoGlow y empieza a recibir reservas online en menos de 90 segundos.</p>
          <div className="mt-5 flex items-center justify-center gap-4">
            <Link to="/register" className="ag-btn-gold h-10 px-5 inline-flex items-center gap-2 rounded-md font-bold text-sm" data-testid="city-cta-register">
              <Storefront size={15} weight="fill" /> Probar gratis · 30 días
            </Link>
            <Link to="/marketplace" className="text-sm text-[var(--ag-text-dim)] hover:text-white underline underline-offset-4" data-testid="city-marketplace-link">
              Ver todas las ciudades
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
